import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  firstValueFrom,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { makePermissionsComplete } from 'server/helpers/permissions/permissions';
import { Customer } from 'server/shared/models/customer';
import {
  CompleteDepartmentPermissions,
  CompleteDispoPermissions,
} from 'server/shared/models/dispoPermissions';
import { Job } from 'server/shared/models/job';
import { RoleEnum } from 'server/shared/models/role.enum';
import { DispoUser, User } from 'server/shared/models/user';
import { CustomerService } from './customer.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService implements OnDestroy {
  isTargetDispo: boolean;
  loggedInUser: User | undefined;
  private destroyed$ = new Subject<void>();

  completePermissions: CompleteDispoPermissions | undefined;

  completePermissions$ = this.userService.readyDispo$.pipe(
    tap((user: DispoUser) => {
      this.completePermissions = makePermissionsComplete(user.dispoPermissions);
    }),
    map((user: DispoUser) => {
      return makePermissionsComplete(user.dispoPermissions);
    }),
  );
  // pooler observables
  canCreatePoolerUsers$ = this.completePermissions$.pipe(
    map((completePermissions) => completePermissions.poolerAccess.create),
  );
  canReadPoolerUsers$ = this.completePermissions$.pipe(
    map((completePermissions) => completePermissions.poolerAccess.read),
  );
  canUpdatePoolerUsers$ = this.completePermissions$.pipe(
    map((completePermissions) => completePermissions.poolerAccess.update),
  );
  canDeletePoolerUsers$ = this.completePermissions$.pipe(
    map((completePermissions) => completePermissions.poolerAccess.delete),
  );
  // dispo observables
  canCreateDispoUsers$ = this.completePermissions$.pipe(
    map((completePermissions) => completePermissions.dispoAccess.create),
  );
  canReadDispoUsers$ = this.completePermissions$.pipe(
    map((completePermissions) => completePermissions.dispoAccess.read),
  );
  canUpdateDispoUsers$ = this.completePermissions$.pipe(
    map((completePermissions) => completePermissions.dispoAccess.update),
  );
  canDeleteDispoUsers$ = this.completePermissions$.pipe(
    map((completePermissions) => completePermissions.dispoAccess.delete),
  );
  // department lead observables
  canCreateRequesterUsers$ = this.completePermissions$.pipe(
    map((completePermissions) => completePermissions.requesterAccess.create),
  );
  canReadRequesterUsers$ = this.completePermissions$.pipe(
    map((completePermissions) => completePermissions.requesterAccess.read),
  );
  canUpdateRequesterUsers$ = this.completePermissions$.pipe(
    map((completePermissions) => completePermissions.requesterAccess.update),
  );
  canDeleteRequesterUsers$ = this.completePermissions$.pipe(
    map((completePermissions) => completePermissions.requesterAccess.delete),
  );
  // manage permissions
  canReadPermissions$ = this.completePermissions$.pipe(
    map(
      (completePermissions) =>
        completePermissions.dispoAccess.featurePermissions.managePermissions
          .read,
    ),
  );

  // transcribe job list permissions (only for DISPO users)
  canReadTranscribeJobList$ = this.userService.readyDispo$.pipe(
    map((user) => {
      const completePermissions = makePermissionsComplete(
        user.dispoPermissions,
      );
      return !!completePermissions.dispoAccess.featurePermissions
        .manageTranscribeJobListPermissions.read;
    }),
  );
  canUpdateTranscribeJobList$ = this.userService.readyDispo$.pipe(
    map((user) => {
      const completePermissions = makePermissionsComplete(
        user.dispoPermissions,
      );
      return !!completePermissions.dispoAccess.featurePermissions
        .manageTranscribeJobListPermissions.update;
    }),
  );

  isCustomerAdmin$ = this.completePermissions$.pipe(
    map((completePermissions) => {
      return completePermissions.dispoAccess.featurePermissions.isCustomerAdmin;
    }),
  );

  userIsCustomerAdmin$ = this.userService.readyDispo$.pipe(
    map((user) => {
      const completePermissions = makePermissionsComplete(
        user.dispoPermissions,
      );
      return (
        user.isAdmin ||
        completePermissions.dispoAccess.featurePermissions.isCustomerAdmin
      );
    }),
  );

  userIsSuperAdmin$ = this.userService.readyDispo$.pipe(
    map((user) => {
      return user.isAdmin;
    }),
  );

  canAccessReports$ = this.userService.readyDispoOrRequester$.pipe(
    switchMap((user) => {
      if (user?.role === RoleEnum.DISPO) {
        return of(true);
      }
      if (user?.role === RoleEnum.REQUESTER) {
        return this.customerService.readyDispoCustomer$.pipe(
          map((customer) => customer?.requestersCanAccessReports || false),
        );
      }
      return of(false);
    }),
  );

  permissionsReady = firstValueFrom(this.completePermissions$);

  constructor(
    private route: ActivatedRoute,
    private userService: UserService,
    private customerService: CustomerService,
  ) {
    this.isTargetDispo = !!this.route.snapshot.queryParams['isTargetDispo'];
    this.userService.readyUser$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.loggedInUser = user;
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  //DispoAccessPermissions
  canReadManagePermissions(): boolean {
    return !!this.completePermissions?.dispoAccess.featurePermissions
      .managePermissions.read;
  }
  canUpdateManagePermissions(): boolean {
    return !!this.completePermissions?.dispoAccess.featurePermissions
      .managePermissions.update;
  }

  canReadTranscribeJobList(): boolean {
    if (this.loggedInUser?.role !== RoleEnum.DISPO) return false;
    return !!this.completePermissions?.dispoAccess.featurePermissions
      .manageTranscribeJobListPermissions.read;
  }
  canUpdateTranscribeJobList(): boolean {
    if (this.loggedInUser?.role !== RoleEnum.DISPO) return false;
    return !!this.completePermissions?.dispoAccess.featurePermissions
      .manageTranscribeJobListPermissions.update;
  }

  //poolerAccess
  canReadPoolerUsers(): boolean {
    return !!this.completePermissions?.poolerAccess.read;
  }
  canCreatePoolerUsers(): boolean {
    return !!this.completePermissions?.poolerAccess.create;
  }
  canUpdatePoolerUsers(): boolean {
    return !!this.completePermissions?.poolerAccess.update;
  }
  canDeletePoolerUsers(): boolean {
    return !!this.completePermissions?.poolerAccess.delete;
  }
  canReadDispoUsers(): boolean {
    return !!this.completePermissions?.dispoAccess.read;
  }
  canCreateDispoUsers(): boolean {
    return !!this.completePermissions?.dispoAccess.create;
  }
  canUpdateDispoUsers(): boolean {
    return !!this.completePermissions?.dispoAccess.update;
  }
  canDeleteDispoUsers(): boolean {
    return !!this.completePermissions?.dispoAccess.delete;
  }
  canReadRequesterUsers(): boolean {
    return !!this.completePermissions?.requesterAccess?.read;
  }
  canCreateRequesterUsers(): boolean {
    return !!this.completePermissions?.requesterAccess?.create;
  }
  canUpdateRequesterUsers(): boolean {
    return !!this.completePermissions?.requesterAccess?.update;
  }
  canDeleteRequesterUsers(): boolean {
    return !!this.completePermissions?.requesterAccess?.delete;
  }

  userHasEditJobPermission$(): Observable<boolean> {
    return this.completePermissions$.pipe(
      map(() => this.userHasEditJobPermission()),
    );
  }

  userHasEditJobPermission(): boolean {
    if (!this.completePermissions) {
      return false;
    }
    return (
      this.completePermissions.departments.writeAll ||
      Object.values(
        this.completePermissions.departments.specificDepartmentIds,
      ).some((department) => department.write)
    );
  }

  userHasDepartmentWritePermission(
    departmentId: string,
    permissions: CompleteDepartmentPermissions,
  ): boolean {
    return !!(
      permissions.writeAll ||
      permissions.specificDepartmentIds[departmentId]?.write
    );
  }

  // departmentAccess
  userCanEditJob(job: Job, permissions: CompleteDispoPermissions): boolean {
    return this.userHasDepartmentWritePermission(
      job.department.id,
      permissions.departments,
    );
  }

  // not all cases are handled as most are covered by the publication / the job does not end up here
  customerCanEditJob(customer: Customer, job: Job): boolean {
    const isOwner = job.ownerCustomerIds?.includes(customer._id);
    const isCreator = job.customer.id === customer._id;
    // we currently only exclude the owner access
    return !(isOwner && !isCreator);
  }
}
